import { useState, useEffect } from 'react';
import { getBinConfigurations, completeStep4 } from '../../services/api';

// ===========================================
// CONFIGURATION: Set to true to show bin calculations, false for guidance
// This allows easy switching between the two views
const SHOW_BIN_CALCULATIONS = false;
// ===========================================

// Guidance content - can be made state-specific in the future
const GUIDANCE_CONTENT = {
  default: {
    intro: {
      title: "General Waste Planning Guidance",
      text: "The following guidance is provided to assist designers, builders and project teams when determining appropriate waste management arrangements. Final waste servicing outcomes should be confirmed with the nominated waste contractor during detailed design."
    },
    sections: [
      {
        title: "Selecting Bin Sizes and Waste Storage Capacity",
        color: "blue",
        content: "For residential developments utilising a shared bin room, the following bin sizes are generally recommended to balance capacity, safety and ease of handling:",
        bullets: [
          "Organics / Green Waste: 120L or 240L bins",
          "General Waste: 240L or 660L bins",
          "Recycling: 660L or 1,100L bins"
        ],
        highlights: [
          "Bin selection should consider physical handling requirements, particularly where larger bins are proposed or where residents may have limited mobility",
          "Collection frequency is critical — reduced collection frequency may require increased on-site storage capacity, and vice versa",
          "Kerbside collection is often preferred due to reduced design complexity; however, it is not always supported by councils for larger developments",
          "Developments requiring bulk bins or approximately 12 or more standard mobile bins, or bins of 660 litres or greater, will typically require on-site waste collection due to safety, operational and amenity considerations"
        ]
      },
      {
        title: "Deciding Bin Transport and Waste Collection Location",
        color: "green",
        content: "Where bins are required to be manually transported distances of approximately 10 to 30 metres or greater, particularly for larger bins such as 660L or 1,100L, consideration should be given to mechanically assisted transport, such as electric bin tugs or similar devices, to reduce manual handling risks.",
        bullets: [
          "Whether bins are presented at the kerbside or serviced within the site",
          "Safe and unobstructed access for waste collection vehicles",
          "Suitable access routes, turning paths and loading areas for the largest collection vehicle",
          "Adequate vertical clearances, gradients and pavement strengths along collection routes"
        ],
        bulletIntro: "Waste collection arrangements should also consider:"
      },
      {
        title: "Future Waste Considerations",
        color: "amber",
        highlights: [
          "Councils are progressively moving toward the collection of additional waste streams, including FOGO (Food Organics and Green Organics) and Glass",
          "While most councils currently mandate General Waste and Recycling only, future servicing requirements should be considered during design to allow for system adaptability"
        ]
      },
      {
        title: "General Note",
        color: "gray",
        content: "This guidance supports informed design and operational decision-making. Final waste management arrangements are to be confirmed prior to occupation to ensure safe, efficient and compliant waste servicing and trouble-free council compliance."
      }
    ]
  }
  // Future: Add state-specific content here
  // VIC: { ... },
  // NSW: { ... },
  // QLD: { ... },
};

// Color configurations for section headers
const SECTION_COLORS = {
  blue: { bg: 'bg-blue-50', border: 'border-blue-200', title: 'text-blue-900', bullet: 'text-blue-500' },
  green: { bg: 'bg-green-50', border: 'border-green-200', title: 'text-green-900', bullet: 'text-green-500' },
  amber: { bg: 'bg-amber-50', border: 'border-amber-200', title: 'text-amber-900', bullet: 'text-amber-500' },
  gray: { bg: 'bg-gray-50', border: 'border-gray-200', title: 'text-gray-900', bullet: 'text-gray-500' },
};

// Guidance Section Component - styled like Step 6 "Before You Download"
const GuidanceSection = ({ title, content, bullets, bulletIntro, highlights, color = 'blue' }) => {
  const colors = SECTION_COLORS[color] || SECTION_COLORS.blue;
  
  return (
    <div className={`border ${colors.border} rounded-lg overflow-hidden`}>
      <div className={`${colors.bg} px-4 py-3 border-b ${colors.border}`}>
        <h3 className={`text-lg font-semibold ${colors.title}`}>{title}</h3>
      </div>
      <div className="p-4 space-y-3">
        {content && (
          <p className="text-gray-700">{content}</p>
        )}
        
        {bulletIntro && (
          <p className="text-sm font-medium text-gray-900">{bulletIntro}</p>
        )}
        
        {bullets && bullets.length > 0 && (
          <div className="bg-gray-50 rounded-lg p-4">
            <ul className="text-sm text-gray-700 space-y-2">
              {bullets.map((bullet, idx) => (
                <li key={idx} className="flex items-start">
                  <span className={`${colors.bullet} mr-2 font-bold`}>•</span>
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {highlights && highlights.length > 0 && (
          <div className="space-y-2">
            {highlights.map((highlight, idx) => (
              <div key={idx} className="flex items-start bg-gray-50 rounded-lg p-3">
                <svg className={`w-5 h-5 ${colors.bullet} mr-2 flex-shrink-0 mt-0.5`} fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <span className="text-sm text-gray-700">{highlight}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Helper function to format bin size display
const formatBinSize = (streamData) => {
  if (!streamData) return '0L';
  
  // Check if this is a custom configuration with bin_breakdown
  if (streamData.bin_breakdown && streamData.bin_breakdown.length > 0) {
    return streamData.bin_breakdown
      .filter(b => b.count > 0)
      .map(b => `${b.size}L`)
      .join(', ') || '0L';
  }
  
  // Standard configuration with single bin_size
  return `${streamData.bin_size || 0}L`;
};

// Reusable component to render a single configuration option
const ConfigurationCard = ({ config, index, title, thirdStreamLabel = "Organics" }) => (
  <div className="border-2 border-gray-300 rounded-lg p-6 hover:border-blue-400 transition-colors">
    <div className="flex justify-between items-start mb-4">
      <div>
        <h3 className="text-lg font-bold text-gray-900">{title || `Option ${index + 1}`}</h3>
        <p className="text-sm text-gray-600">{config.description}</p>
      </div>
      <div className="text-right">
        <p className="text-2xl font-bold text-blue-600">{config.total_bins}</p>
        <p className="text-xs text-gray-600">Total Bins</p>
      </div>
    </div>

    {/* Waste Streams Table */}
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Stream</th>
            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Bin Size</th>
            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Bin No.</th>
            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Weekly Cap.</th>
            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Weekly Gen.</th>
            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Collections/Wk</th>
            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Storage (m²)</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          <tr>
            <td className="px-3 py-2 text-sm font-medium text-gray-900">General Waste</td>
            <td className="px-3 py-2 text-sm text-gray-600 text-right">{formatBinSize(config.streams.general_waste)}</td>
            <td className="px-3 py-2 text-sm text-gray-900 text-right font-medium">{config.streams.general_waste.bins_needed}</td>
            <td className="px-3 py-2 text-sm text-gray-600 text-right">{config.streams.general_waste.weekly_capacity}L</td>
            <td className="px-3 py-2 text-sm text-gray-600 text-right">{config.streams.general_waste.weekly_generation}L</td>
            <td className="px-3 py-2 text-sm text-gray-900 text-right font-medium">{config.streams.general_waste.collections_per_week}</td>
            <td className="px-3 py-2 text-sm text-gray-600 text-right">{config.streams.general_waste.floor_space} m²</td>
          </tr>
          <tr>
            <td className="px-3 py-2 text-sm font-medium text-gray-900">Recycling</td>
            <td className="px-3 py-2 text-sm text-gray-600 text-right">{formatBinSize(config.streams.recycling)}</td>
            <td className="px-3 py-2 text-sm text-gray-900 text-right font-medium">{config.streams.recycling.bins_needed}</td>
            <td className="px-3 py-2 text-sm text-gray-600 text-right">{config.streams.recycling.weekly_capacity}L</td>
            <td className="px-3 py-2 text-sm text-gray-600 text-right">{config.streams.recycling.weekly_generation}L</td>
            <td className="px-3 py-2 text-sm text-gray-900 text-right font-medium">{config.streams.recycling.collections_per_week}</td>
            <td className="px-3 py-2 text-sm text-gray-600 text-right">{config.streams.recycling.floor_space} m²</td>
          </tr>
          <tr>
            <td className="px-3 py-2 text-sm font-medium text-gray-900">{thirdStreamLabel}</td>
            <td className="px-3 py-2 text-sm text-gray-600 text-right">{formatBinSize(config.streams.organics)}</td>
            <td className="px-3 py-2 text-sm text-gray-900 text-right font-medium">{config.streams.organics.bins_needed}</td>
            <td className="px-3 py-2 text-sm text-gray-600 text-right">{config.streams.organics.weekly_capacity}L</td>
            <td className="px-3 py-2 text-sm text-gray-600 text-right">{config.streams.organics.weekly_generation}L</td>
            <td className="px-3 py-2 text-sm text-gray-900 text-right font-medium">{config.streams.organics.collections_per_week}</td>
            <td className="px-3 py-2 text-sm text-gray-600 text-right">{config.streams.organics.floor_space} m²</td>
          </tr>
        </tbody>
      </table>
    </div>

    {/* Storage Summary */}
    <div className="mt-4 bg-blue-50 border border-blue-200 rounded-md p-4">
      <div className="grid grid-cols-3 gap-4 text-center">
        <div>
          <p className="text-xs text-blue-700">Total Floor Space</p>
          <p className="text-lg font-bold text-blue-900">{config.total_floor_space} m²</p>
        </div>
        <div>
          <p className="text-xs text-blue-700">+ 20% Circulation</p>
          <p className="text-lg font-bold text-blue-900">+{(config.storage_area - config.total_floor_space).toFixed(2)} m²</p>
        </div>
        <div>
          <p className="text-xs text-blue-700">Total Storage Area</p>
          <p className="text-lg font-bold text-blue-900">{config.storage_area} m²</p>
        </div>
      </div>
    </div>
  </div>
);

const Step4BinCalculations = ({ project, onComplete, onBack }) => {
  const [configurations, setConfigurations] = useState([]);
  const [residentialConfigurations, setResidentialConfigurations] = useState([]);
  const [commercialConfigurations, setCommercialConfigurations] = useState([]);
  const [isMixedUse, setIsMixedUse] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Determine if this is a Medical Centre project (third stream = Clinical Waste)
  const isMedicalFacility = () => {
    const commercialOps = project?.step2_data?.commercial_operations || [];
    return commercialOps.some(op => {
      const opType = (op.operation_type || '').toLowerCase();
      return opType.includes('medical') || 
             opType.includes('health') ||
             opType.includes('hospital') ||
             opType.includes('healthcare');
    });
  };

  const getThirdStreamLabel = () => {
    return isMedicalFacility() ? "Clinical Waste" : "Organics";
  };

  // Get guidance content based on state (future: make state-specific)
  const getGuidanceContent = () => {
    const stateCode = project?.state_code || 'default';
    return GUIDANCE_CONTENT[stateCode] || GUIDANCE_CONTENT.default;
  };

  useEffect(() => {
    // Always load configurations in the background to maintain architecture
    loadConfigurations();
  }, [project]);

  const loadConfigurations = async () => {
    try {
      setLoading(true);
      const response = await getBinConfigurations(project.id);
      
      if (response.is_mixed_use) {
        setIsMixedUse(true);
        setResidentialConfigurations(response.residential_configurations || []);
        setCommercialConfigurations(response.commercial_configurations || []);
      } else {
        setIsMixedUse(false);
        setConfigurations(response.configurations || []);
      }
    } catch (err) {
      console.error('Failed to calculate bin configurations:', err);
      // Don't show error for guidance view - calculations happen in background
      if (SHOW_BIN_CALCULATIONS) {
        setError('Failed to calculate bin configurations. Please ensure Step 3 is completed.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = async () => {
    try {
      setSaving(true);
      // Mark Step 4 as complete on the backend
      const updatedProject = await completeStep4(project.id);
      onComplete(updatedProject);
    } catch (err) {
      console.error('Failed to complete step 4:', err);
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Show loading only for bin calculations view
  if (loading && SHOW_BIN_CALCULATIONS) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Calculating bin configurations...</p>
      </div>
    );
  }

  const hasConfigurations = isMixedUse 
    ? (residentialConfigurations.length > 0 || commercialConfigurations.length > 0)
    : configurations.length > 0;

  // Show error only for bin calculations view
  if (SHOW_BIN_CALCULATIONS && (error || !hasConfigurations)) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <div className="text-red-600 mb-4">
          <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">Unable to Calculate Bins</h2>
        <p className="text-gray-600 mb-4">{error || 'Please complete Step 3 before proceeding.'}</p>
        <button
          onClick={onBack}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Go Back to Step 3
        </button>
      </div>
    );
  }

  // ============================================
  // GUIDANCE VIEW (New default view)
  // ============================================
  if (!SHOW_BIN_CALCULATIONS) {
    const guidance = getGuidanceContent();
    
    return (
      <div className="bg-white rounded-lg shadow">
        <div className="px-8 py-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={onBack}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Step 4: {guidance.intro.title}</h2>
          <p className="mt-2 text-gray-600">
            {guidance.intro.text}
          </p>
        </div>

        <div className="px-8 py-6 space-y-6">
          {guidance.sections.map((section, index) => (
            <GuidanceSection
              key={index}
              title={section.title}
              content={section.content}
              bullets={section.bullets}
              bulletIntro={section.bulletIntro}
              highlights={section.highlights}
              color={section.color}
            />
          ))}
        </div>

        {/* Navigation Buttons */}
        <div className="px-8 py-6 border-t border-gray-200 flex justify-between">
          <button
            type="button"
            onClick={onBack}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Back
          </button>
          <button
            onClick={handleContinue}
            disabled={saving}
            className="px-6 py-2 bg-blue-600 text-white rounded-md shadow-sm text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Continue to Waste Calculations'}
          </button>
        </div>
      </div>
    );
  }

  // ============================================
  // BIN CALCULATIONS VIEW (Original view - preserved)
  // ============================================
  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-8 py-6 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Step 4: General Waste Planning Guidance</h2>
        <p className="mt-1 text-sm text-gray-600">
          {isMixedUse 
            ? 'Review the bin configuration options below for both Residential and Commercial waste. You\'ll select your preferred options in the next step.'
            : 'Review the three bin configuration options below. You\'ll select your preferred option in the next step.'
          }
        </p>
      </div>

      <div className="px-8 py-6 space-y-8">
        {isMixedUse ? (
          <>
            {/* Mixed-Use Info Banner */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-start">
                <svg className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div className="ml-3">
                  <h4 className="text-sm font-semibold text-purple-800">Mixed-Use Development</h4>
                  <p className="text-sm text-purple-700 mt-1">
                    Residential and Commercial waste are calculated separately. Residential waste will be collected by Council, 
                    while Commercial waste will be collected by Private contractors.
                  </p>
                </div>
              </div>
            </div>

            {/* Residential Bins Section */}
            <div>
              <div className="flex items-center mb-4">
                <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center mr-3">
                  <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Residential Bins</h3>
                  <p className="text-sm text-gray-600">Collected by Council • {project.collections_per_week || 1}x per week</p>
                </div>
              </div>
              <div className="space-y-4">
                {residentialConfigurations.map((config, index) => (
                  <ConfigurationCard 
                    key={`res-${index}`} 
                    config={config} 
                    index={index}
                    title={`Residential Option ${index + 1}`}
                    thirdStreamLabel={getThirdStreamLabel()}
                  />
                ))}
              </div>
            </div>

            {/* Commercial Bins Section */}
            <div>
              <div className="flex items-center mb-4">
                <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                  <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Commercial Bins</h3>
                  <p className="text-sm text-gray-600">Collected by Private Contractor • {project.commercial_collections_per_week || 1}x per week</p>
                </div>
              </div>
              <div className="space-y-4">
                {commercialConfigurations.map((config, index) => (
                  <ConfigurationCard 
                    key={`com-${index}`} 
                    config={config} 
                    index={index}
                    title={`Commercial Option ${index + 1}`}
                    thirdStreamLabel={getThirdStreamLabel()}
                  />
                ))}
              </div>
            </div>
          </>
        ) : (
          /* Standard Single-Use Display */
          <div className="space-y-6">
            {configurations.map((config, index) => (
              <ConfigurationCard 
                key={index} 
                config={config} 
                index={index}
                thirdStreamLabel={getThirdStreamLabel()}
              />
            ))}
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="px-8 py-6 border-t border-gray-200 flex justify-between">
        <button
          type="button"
          onClick={onBack}
          className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Back
        </button>
        <button
          onClick={handleContinue}
          disabled={saving}
          className="px-6 py-2 bg-blue-600 text-white rounded-md shadow-sm text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Continue to Waste Calculations'}
        </button>
      </div>
    </div>
  );
};

export default Step4BinCalculations;
